/**
 * Role Guard
 *
 * Protects routes based on user roles.
 * User must be authenticated AND have one of the allowed roles.
 *
 * Usage:
 * ```tsx
 * // Single role
 * <RoleGuard allowedRoles={["admin"]}>
 *   <AdminDashboard />
 * ./role-guard>
 *
 * // Multiple roles (OR logic)
 * <RoleGuard allowedRoles={["admin", "manager"]}>
 *   <ManagementPanel />
 * ./role-guard>
 *
 * // With custom unauthorized redirect
 * <RoleGuard allowedRoles={["admin"]} redirectTo="/access-denied">
 *   <SecretPage />
 * ./role-guard>
 * ```
 */

import { Navigate, useLocation } from "react-router-dom"

import { useAuth } from "@/contexts/auth-context"
import { useUser } from "@/contexts/user-context"

import { LoadingFallback } from "./loading-fallback"

import type { RoleGuardProps } from "./types"
import { DEFAULT_ROUTES } from "./types"

export function RoleGuard({
  children,
  allowedRoles,
  redirectTo = DEFAULT_ROUTES.unauthorized,
  fallback,
}: RoleGuardProps) {
  const { isAuthenticated, isLoading } = useAuth()
  const { roles } = useUser()
  const location = useLocation()

  if (isLoading) {
    return fallback ? <>{fallback}</> : <LoadingFallback />
  }

  // Not authenticated - redirect to sign-in first
  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search
    const searchParams = new URLSearchParams({ redirectTo: currentPath })
    return <Navigate to={`${DEFAULT_ROUTES.signIn}?${searchParams.toString()}`} replace />
  }

  // Access check (any-role) is enforced by AccessGate above this guard.
  // Here we only check whether the user has one of the route-specific roles.
  const hasRequiredRole = roles.some((r) => allowedRoles.includes(r as never))
  if (!hasRequiredRole) {
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}
